import { CustomErrorBoundary } from '@/components/ErrorBoundary';
import { VendorFeatureGate } from '@/components/feature-flags/VendorFeatureGate';
import { SplashScreen } from '@/components/loading-screen';
import { ORDER_CREATE_PERMISSION_PREFIXES } from '@/constants/permissions';
import PermissionGuard from '@/context/auth/PermissionGuard';
import PermissionRoleGuard from '@/context/auth/PermissionRoleGuard';
import RoleGuard from '@/context/auth/RoleGuard';
import { FeatureFlagsProvider } from '@/context/FeatureFlagsContext';
import { CustomerPermissionsRole, CustomerType } from '@/enums';
import GlobalSettingsPage from '@/pages/global-settings';
import { lazy, Suspense } from 'react';
import { Navigate, Outlet } from 'react-router';

import App from '../App';

const Home = lazy(() => import('../pages/Home'));
const Orders = lazy(() => import('../pages/orders/Orders'));
const Lenders = lazy(() => import('../pages/lenders'));
const CreateOrder = lazy(() => import('../pages/orders/OrderWizard'));
const BulkUpload = lazy(() => import('../pages/orders/BulkUploader'));
const OrderConfirmationSuccess = lazy(() => import('../pages/orders/OrderConfirmationSuccess'));
const Vendors = lazy(() => import('../pages/Vendors'));
const Reports = lazy(() => import('../pages/Reports'));
const AllocationPage = lazy(() => import('../features/allocation/pages/AllocationPage'));
const AllocationLayout = lazy(() => import('../features/allocation/layout'));
const NotFound = lazy(() => import('../pages/NotFound'));
const OrderDetails = lazy(() => import('../pages/orders/order-details/OrderDetails'));
const OrdersLayout = lazy(() => import('../pages/orders/layout'));
const MasterOrderDetails = lazy(() => import('../pages/master-order-details/MasterOrderDetails'));
const Forbidden = lazy(() => import('../pages/Forbidden'));
const LenderDetails = lazy(() => import('../pages/lenders/details/LenderDetails'));
const BranchDetails = lazy(() => import('../pages/branches/BranchDetails'));
const VendorDetails = lazy(() => import('../pages/vendors/VendorDetails'));
const ManageVendorProduct = lazy(() => import('../pages/vendors/product'));
const Ice = lazy(() => import('../pages/embeddable/Ice'));
const MLink = lazy(() => import('../pages/embeddable/MLink'));
const CreateBanners = lazy(() => import('../pages/banners/CreateBanners'));
const ViewBanners = lazy(() => import('../pages/banners/ViewBanners'));
const SelectAudience = lazy(() => import('../pages/banners/SelectAudience'));
const WorkflowList = lazy(() => import('../pages/workflow/WorkflowList'));
export const AppRoutes = [
  {
    element: (
      <Suspense fallback={<SplashScreen />}>
        <CustomErrorBoundary>
          <FeatureFlagsProvider>
            <App />
          </FeatureFlagsProvider>
        </CustomErrorBoundary>
      </Suspense>
    ),
    children: [
      { path: '/', element: <Home />, index: true },
      {
        element: <OrdersLayout />,
        children: [
          {
            path: 'orders',
            children: [
              { index: true, element: <Orders /> },
              {
                path: 'create',
                element: (
                  <RoleGuard role={CustomerType.lender} redirect="/forbidden">
                    <PermissionGuard prefixes={ORDER_CREATE_PERMISSION_PREFIXES} redirect="/forbidden">
                      <CreateOrder />
                    </PermissionGuard>
                  </RoleGuard>
                ),
              },
              { path: 'upload', element: <BulkUpload /> },
              { path: ':id', element: <OrderDetails /> },
              { path: 'confirmation/success', element: <OrderConfirmationSuccess /> },
            ],
          },
          { path: 'masterOrders/:masterOrderId/:masterOrderCode', element: <MasterOrderDetails /> },
        ],
      },
      { path: 'banners/create', element: <CreateBanners /> },
      { path: 'banners/edit', element: <CreateBanners /> },
      { path: 'banners/view', element: <ViewBanners /> },
      { path: 'banners/select-audience', element: <SelectAudience /> },
      {
        path: 'lenders',
        element: (
          <RoleGuard redirect="/forbidden" role={CustomerType.admin}>
            <Lenders />
          </RoleGuard>
        ),
      },
      {
        path: 'lenders/:lenderOrgCodeParam',
        element: (
          <RoleGuard redirect="/forbidden" role={CustomerType.admin}>
            <LenderDetails />
          </RoleGuard>
        ),
      },
      {
        path: 'lenders/:lenderOrgCodeParam/branch/:branchOrgCodeParam',
        element: (
          <RoleGuard redirect="/forbidden" role={CustomerType.admin}>
            <BranchDetails />
          </RoleGuard>
        ),
      },
      {
        path: 'vendors',
        element: (
          <VendorFeatureGate>
            <RoleGuard redirect="/forbidden" role={CustomerType.admin}>
              <Vendors />
            </RoleGuard>
          </VendorFeatureGate>
          ),
      },
      {
        path: 'vendors/:organizationCode',
        element: (
          <VendorFeatureGate>
            <RoleGuard redirect="/forbidden" role={CustomerType.admin}>
              <VendorDetails />
            </RoleGuard>
          </VendorFeatureGate>
          ),
      },
      {
        path: 'vendors/:organizationCode/product/:productCode',
        element: (
          <VendorFeatureGate>
            <RoleGuard redirect="/forbidden" role={CustomerType.admin}>
              <ManageVendorProduct />
            </RoleGuard>
          </VendorFeatureGate>
          ),
      },
      {
        path: 'reports',
        element: (
          <RoleGuard redirect="/forbidden" role={CustomerType.admin}>
            <Reports />
          </RoleGuard>
        ),
      },
      {
        path: 'allocation',
        element: (
          <PermissionRoleGuard
            permissionsRole={CustomerPermissionsRole.AllocationAdmin}
            redirect="/forbidden"
          >
            <AllocationLayout />
          </PermissionRoleGuard>
        ),
        children: [
          { index: true, element: <AllocationPage /> },
        ],
      },
      {
        path: 'global-settings',
        element: (
          <RoleGuard redirect="/forbidden" role={CustomerType.admin}>
            <GlobalSettingsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'workflow',
        element: (
          <RoleGuard redirect="/forbidden" role={CustomerType.admin}>
            <WorkflowList mode="global" />
          </RoleGuard>
        ),
      },
      {
        path: 'tenant-config',
        element: <Navigate to="/forbidden" replace />,
      },
      { path: '*', element: <NotFound /> },
    ],
  },
  {
    element: (
      <Suspense fallback={<SplashScreen />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      { path: 'forbidden', element: <Forbidden /> },
      { path: 'embeddable/ice', element: <Ice /> },
    ],
  },
  {
    element: (
      <Suspense fallback={<SplashScreen />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      { path: 'forbidden', element: <Forbidden /> },
      { path: 'embeddable/mlink', element: <MLink /> },
    ],
  },
];
